/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { visitsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { HomlyLogo } from '../../components/brand/HomlyLogo';
import {
    Star, MapPin, Send, CheckCircle, Loader2, XCircle,
    Home, Sun, Wrench, ThumbsUp, ThumbsDown, HelpCircle,
    Clock, HandCoins, MessageSquare, CreditCard,
    ArrowRight
} from 'lucide-react';

const StarRating = ({ value, onChange, label, icon: Icon }) => (
    <div className="space-y-2">
        <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#FF914D]" />
            <span className="text-sm font-semibold text-white">{label}</span>
        </div>
        <div className="flex gap-1.5">
            {[1, 2, 3, 4, 5].map(s => (
                <button
                    key={s} type="button" onClick={() => onChange(s)}
                    className="p-1 transition-transform hover:scale-110 active:scale-95"
                    data-testid={`star-${label}-${s}`}
                >
                    <Star className={`w-8 h-8 ${s <= value ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'} transition-colors`} />
                </button>
            ))}
        </div>
    </div>
);

const TriChoice = ({ value, onChange, label, icon: Icon }) => (
    <div className="space-y-2">
        <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#FF914D]" />
            <span className="text-sm font-semibold text-white">{label}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
            {[
                { v: 'oui', label: 'Oui', icon: ThumbsUp, cls: 'border-green-500/30 bg-green-500/10 text-green-400' },
                { v: 'partiellement', label: 'Moyen', icon: HelpCircle, cls: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
                { v: 'non', label: 'Non', icon: ThumbsDown, cls: 'border-red-500/30 bg-red-500/10 text-red-400' },
            ].map(opt => (
                <button
                    key={opt.v} type="button" onClick={() => onChange(opt.v)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                        value === opt.v ? opt.cls + ' scale-[1.02]' : 'border-white/10 bg-white/5 text-white/50'
                    }`}
                    data-testid={`choice-${label}-${opt.v}`}
                >
                    <opt.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{opt.label}</span>
                </button>
            ))}
        </div>
    </div>
);

export default function VisitFormPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [sending, setSending] = useState(false);
    const [step, setStep] = useState(0);

    const [form, setForm] = useState({
        visitor_first_name: '', visitor_last_name: '',
        visitor_email: '', visitor_phone: '',
        rating_overall: 0, rating_luminosity: 0, rating_condition: 0,
        location_ok: '', price_ok: '',
        purchase_timeline: '', would_make_offer: '',
        has_financing: '',
        comment: '',
    });

    useEffect(() => {
        visitsAPI.getForm(token).then(r => {
            setInfo(r.data);
        }).catch(() => {
            setError("Ce lien de visite est invalide ou a expiré.");
        }).finally(() => setLoading(false));
        // Note: setInfo/setError/setLoading are React state setters — guaranteed stable
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const canNext = () => {
        if (step === 0) return form.visitor_first_name.trim() && form.visitor_last_name.trim();
        if (step === 1) return form.rating_overall > 0 && form.rating_luminosity > 0 && form.rating_condition > 0;
        if (step === 2) return form.location_ok && form.price_ok;
        if (step === 3) return form.purchase_timeline && form.would_make_offer && form.has_financing;
        return true;
    };

    const handleSubmit = async () => {
        setSending(true);
        try {
            await visitsAPI.submitReport(token, form);
            setSubmitted(true);
        } catch {
            setError("Erreur lors de l'envoi. Veuillez réessayer.");
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF914D]" />
            </div>
        );
    }

    if (error && !info) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950 flex items-center justify-center p-4">
                <div className="text-center">
                    <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h1 className="text-xl font-bold text-white mb-2">Lien invalide</h1>
                    <p className="text-white/50">{error}</p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950 flex items-center justify-center p-4">
                <div className="text-center max-w-sm">
                    <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 animate-[bounce_0.6s_ease-in-out]">
                        <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h1 className="text-2xl font-bold text-white mb-3">Merci pour votre retour !</h1>
                    <p className="text-white/60 mb-2">Votre avis a été transmis en temps réel au propriétaire et à l'agent immobilier.</p>
                    <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10">
                        <p className="text-white/50 text-sm">
                            {form.would_make_offer === 'oui'
                                ? "Votre intérêt a bien été noté. L'agent vous recontactera très rapidement pour discuter de votre offre."
                                : form.would_make_offer === 'je_reflechis'
                                ? "Prenez le temps de la réflexion. L'agent reste disponible si vous avez des questions."
                                : "Votre retour est précieux pour le propriétaire. Bonne continuation dans vos recherches !"}
                        </p>
                    </div>
                    <div className="mt-8">
                        <HomlyLogo size="sm" />
                    </div>
                </div>
            </div>
        );
    }

    const steps = [
        // Step 0: Identity
        <div key="identity" className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-[#681793]/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#FF914D]">1</span>
                </div>
                <h2 className="text-lg font-bold text-white">Vos coordonnées</h2>
            </div>
            <p className="text-white/40 text-sm mb-4">Pour que l'agent puisse vous recontacter si besoin.</p>
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs text-white/50 mb-1 block">Prénom *</label>
                    <input value={form.visitor_first_name} onChange={e => updateField('visitor_first_name', e.target.value)}
                        className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#681793]/50"
                        placeholder="Jean" data-testid="input-first-name" />
                </div>
                <div>
                    <label className="text-xs text-white/50 mb-1 block">Nom *</label>
                    <input value={form.visitor_last_name} onChange={e => updateField('visitor_last_name', e.target.value)}
                        className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#681793]/50"
                        placeholder="Dupont" data-testid="input-last-name" />
                </div>
            </div>
            <div>
                <label className="text-xs text-white/50 mb-1 block">Email (facultatif)</label>
                <input value={form.visitor_email} onChange={e => updateField('visitor_email', e.target.value)} type="email"
                    className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#681793]/50"
                    placeholder="jean@email.com" data-testid="input-email" />
            </div>
            <div>
                <label className="text-xs text-white/50 mb-1 block">Téléphone (facultatif)</label>
                <input value={form.visitor_phone} onChange={e => updateField('visitor_phone', e.target.value)} type="tel"
                    className="w-full h-11 rounded-xl bg-white/5 border border-white/10 px-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#681793]/50"
                    placeholder="06 12 34 56 78" data-testid="input-phone" />
            </div>
        </div>,

        // Step 1: Ratings
        <div key="ratings" className="space-y-6">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-[#681793]/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#FF914D]">2</span>
                </div>
                <h2 className="text-lg font-bold text-white">Votre impression</h2>
            </div>
            <p className="text-white/40 text-sm mb-4">Notez les éléments suivants de 1 à 5 étoiles.</p>
            <StarRating value={form.rating_overall} onChange={v => updateField('rating_overall', v)} label="Impression générale" icon={Home} />
            <StarRating value={form.rating_luminosity} onChange={v => updateField('rating_luminosity', v)} label="Luminosité" icon={Sun} />
            <StarRating value={form.rating_condition} onChange={v => updateField('rating_condition', v)} label="État général du bien" icon={Wrench} />
        </div>,

        // Step 2: Yes/No questions
        <div key="yesno" className="space-y-6">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-[#681793]/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#FF914D]">3</span>
                </div>
                <h2 className="text-lg font-bold text-white">Le bien correspond ?</h2>
            </div>
            <TriChoice value={form.location_ok} onChange={v => updateField('location_ok', v)} label="L'emplacement vous convient ?" icon={MapPin} />
            <TriChoice value={form.price_ok} onChange={v => updateField('price_ok', v)} label="Le prix vous semble adapté ?" icon={HandCoins} />
        </div>,

        // Step 3: Purchase intent + Financing
        <div key="intent" className="space-y-6">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-[#681793]/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#FF914D]">4</span>
                </div>
                <h2 className="text-lg font-bold text-white">Votre projet</h2>
            </div>
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#FF914D]" />
                    <span className="text-sm font-semibold text-white">Quand souhaitez-vous acheter ?</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { v: 'court_terme', label: 'Très vite', sub: '< 3 mois' },
                        { v: '6_mois', label: 'Bientôt', sub: '3-6 mois' },
                        { v: 'je_regarde', label: 'Je regarde', sub: 'Pas pressé' },
                    ].map(opt => (
                        <button key={opt.v} type="button" onClick={() => updateField('purchase_timeline', opt.v)}
                            className={`p-3 rounded-xl border-2 text-center transition-all ${
                                form.purchase_timeline === opt.v
                                    ? 'border-[#681793]/50 bg-[#681793]/10 text-white'
                                    : 'border-white/10 bg-white/5 text-white/50'
                            }`}
                            data-testid={`timeline-${opt.v}`}>
                            <span className="text-sm font-medium block">{opt.label}</span>
                            <span className="text-[10px] text-white/30">{opt.sub}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <HandCoins className="w-4 h-4 text-[#FF914D]" />
                    <span className="text-sm font-semibold text-white">Souhaitez-vous faire une offre ?</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { v: 'oui', label: 'Oui', icon: ThumbsUp, cls: 'border-green-500/30 bg-green-500/10 text-green-400' },
                        { v: 'je_reflechis', label: "J'y réfléchis", icon: HelpCircle, cls: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
                        { v: 'non', label: 'Non', icon: ThumbsDown, cls: 'border-red-500/30 bg-red-500/10 text-red-400' },
                    ].map(opt => (
                        <button key={opt.v} type="button" onClick={() => updateField('would_make_offer', opt.v)}
                            className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                                form.would_make_offer === opt.v ? opt.cls : 'border-white/10 bg-white/5 text-white/50'
                            }`}
                            data-testid={`offer-${opt.v}`}>
                            <opt.icon className="w-5 h-5" />
                            <span className="text-xs font-medium">{opt.label}</span>
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-[#FF914D]" />
                    <span className="text-sm font-semibold text-white">Avez-vous un financement validé ?</span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { v: 'oui', label: 'Oui', sub: 'Prêt accordé', cls: 'border-green-500/30 bg-green-500/10 text-green-400' },
                        { v: 'en_cours', label: 'En cours', sub: 'Demande faite', cls: 'border-yellow-500/30 bg-yellow-500/10 text-yellow-400' },
                        { v: 'non', label: 'Pas encore', sub: 'À faire', cls: 'border-white/10 bg-white/5 text-white/50' },
                    ].map(opt => (
                        <button key={opt.v} type="button" onClick={() => updateField('has_financing', opt.v)}
                            className={`p-3 rounded-xl border-2 text-center transition-all ${
                                form.has_financing === opt.v ? opt.cls + ' scale-[1.02]' : 'border-white/10 bg-white/5 text-white/50'
                            }`}
                            data-testid={`financing-${opt.v}`}>
                            <span className="text-sm font-medium block">{opt.label}</span>
                            <span className="text-[10px] opacity-60">{opt.sub}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>,

        // Step 4: Comment
        <div key="comment" className="space-y-4">
            <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-full bg-[#681793]/20 flex items-center justify-center">
                    <span className="text-sm font-bold text-[#FF914D]">5</span>
                </div>
                <h2 className="text-lg font-bold text-white">Un commentaire ?</h2>
            </div>
            <p className="text-white/40 text-sm">Facultatif — partagez vos impressions librement.</p>
            <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 text-[#FF914D] mt-3" />
                <textarea value={form.comment} onChange={e => updateField('comment', e.target.value)}
                    className="flex-1 h-28 rounded-xl bg-white/5 border border-white/10 p-3 text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#681793]/50 placeholder:text-white/20"
                    placeholder="Ce que j'ai aime, ce qui m'a moins plu..." data-testid="input-comment" />
            </div>
        </div>,
    ];

    const totalSteps = steps.length;
    const isLast = step === totalSteps - 1;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0f0a1a] to-slate-950" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
            <div className="max-w-md mx-auto px-4 py-6">
                {/* Header */}
                <div className="text-center mb-6">
                    <HomlyLogo size="sm" />
                    <div className="mt-4 p-3 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center gap-2 justify-center text-white/50 text-xs mb-1">
                            <MapPin className="w-3 h-3" />
                            <span>Visite du bien</span>
                        </div>
                        <p className="text-white font-semibold text-sm">{info?.property_address || 'Bien immobilier'}</p>
                        {info?.agent_name && <p className="text-white/40 text-xs mt-0.5">Agent : {info.agent_name}</p>}
                    </div>
                </div>

                {/* Progress */}
                <div className="flex gap-1.5 mb-6">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-all duration-300 ${i <= step ? 'bg-gradient-to-r from-[#681793] to-[#FF914D]' : 'bg-white/10'}`} />
                    ))}
                </div>

                {/* Step indicator */}
                <p className="text-white/30 text-xs mb-4 text-right">Étape {step + 1} / {totalSteps}</p>

                {/* Step Content */}
                <div className="min-h-[300px]">
                    {steps[step]}
                </div>

                {/* Navigation */}
                <div className="flex gap-3 mt-6">
                    {step > 0 && (
                        <Button variant="outline" onClick={() => setStep(s => s - 1)}
                            className="flex-1 h-12 border-white/10 text-white/70 hover:bg-white/5"
                            data-testid="prev-step-btn">
                            Retour
                        </Button>
                    )}
                    {!isLast ? (
                        <Button onClick={() => setStep(s => s + 1)} disabled={!canNext()}
                            className="flex-1 h-12 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold disabled:opacity-40"
                            data-testid="next-step-btn">
                            Suivant
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    ) : (
                        <Button onClick={handleSubmit} disabled={sending}
                            className="flex-1 h-12 bg-gradient-to-r from-[#681793] to-[#FF914D] text-white font-bold"
                            data-testid="submit-report-btn">
                            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                            Envoyer mon avis
                        </Button>
                    )}
                </div>

                {error && <p className="text-red-400 text-sm text-center mt-3">{error}</p>}

                <p className="text-center text-white/20 text-xs mt-6">
                    Vos données sont traitées conformément au RGPD. Elles sont transmises uniquement à l'agent et au propriétaire.
                </p>
            </div>
        </div>
    );
}
